import { useEffect, useState, useCallback } from 'react'
import { FiX } from 'react-icons/fi'

export type ToastType = 'error' | 'success'
export type ToastPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

export interface ToastProps {
  message: string
  type?: ToastType
  duration?: number
  position?: ToastPosition
  onClose: () => void
}

const TOAST_COLORS = {
  error: '#F0D3D5',
  success: '#BDD9D0'
} as const

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'error',
  duration = 5000,
  position = 'bottom-right',
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false)

  const handleClose = useCallback(() => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 300) // Wait for animation to complete
  }, [onClose])

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10)

    // Auto dismiss
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose()
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [duration, handleClose])

  // Determine animation direction based on position
  const getAnimationClass = () => {
    if (!isVisible) {
      if (position.includes('right')) return 'translate-x-full opacity-0'
      if (position.includes('left')) return '-translate-x-full opacity-0'
    }
    return 'translate-x-0 opacity-100'
  }

  return (
    <div
      className={`
        relative flex items-center justify-center rounded-lg shadow-lg
        transition-all duration-300 ease-in-out
        ${getAnimationClass()}
      `}
      style={{
        backgroundColor: TOAST_COLORS[type],
        color: '#000000',
        minWidth: '300px',
        maxWidth: '400px',
        padding: '16px'
      }}
    >
      <button
        onClick={handleClose}
        className="absolute right-2 top-2 transition-opacity hover:opacity-70"
        aria-label="Close"
      >
        <FiX size={16} className="mb-1 text-black" />
      </button>
      <p className="w-full text-center text-sm">{message}</p>
    </div>
  )
}

export default Toast
